
// Boost values for date patterns (higher = more priority)
const BOOST_DATE = 2;  // Date patterns - higher priority than text

// Check if debug mode is enabled (global)
const urlParams = new URLSearchParams(window.location.search);
const debugMode = urlParams.get('debug') || '0';

/**
 * Check if OR condition contains numeric patterns (that could be dates)
 * @param {Object} orCondition - OR condition to check
 * @returns {boolean} - True if condition contains numeric patterns
 */
function hasNumericPatterns(orCondition) {
    if (!orCondition || !orCondition.value || !Array.isArray(orCondition.value)) return false;
    for (const patternItem of orCondition.value) {
        const pattern = (typeof patternItem === 'object' && patternItem.pattern) ? patternItem.pattern : String(patternItem);
        // Check if pattern is numeric (digits only, possibly with word boundaries)
        // Supports standard \b and Unicode boundaries (?<![\p{L}\p{N}]) and (?![\\p{L}\\p{N}])
        const numericPattern = /^(?:\(\?\<\!\[\\p\{L\}\\p\{N\}\]\)|\\b)?\d+(?:\(\?\!\[\\p\{L\}\\p\{N\}\]\)|\\b)?$/i.test(pattern);
        if (numericPattern) return true;
    }
    return false;
}

/**
 * Helper: Check if OR condition contains exact phrase patterns (boost >= 5)
 */
function hasExactPhraseInOrCondition(orCondition) {
    if (!orCondition || !orCondition.value || !Array.isArray(orCondition.value)) return false;
    for (const patternItem of orCondition.value) {
        const boost = (typeof patternItem === 'object' && patternItem.boost) ? patternItem.boost : 1;
        if (boost >= 5) return true;
    }
    return false;
}

/**
 * Helper: Check if OR condition contains any of the specified tokens
 */
function matchesUsedTokens(orCondition, tokens) {
    if (!orCondition || !orCondition.value || !Array.isArray(orCondition.value) || !tokens || tokens.length === 0) return false;
    const tokensSet = new Set(tokens.map(t => t.toLowerCase()));
    
    for (const patternItem of orCondition.value) {
        const pattern = (typeof patternItem === 'object' && patternItem.pattern) ? patternItem.pattern : String(patternItem);
        // Remove boundaries and escapes for matching
        const cleanPattern = pattern
            .replace(/\\/g, '')
            .replace(/\(\?\<\!\[\\p\{L\}\\p\{N\}\]\)/g, '')
            .replace(/\(\?\!\[\\p\{L\}\\p\{N\}\]\)/g, '')
            .replace(/\(\?\<\!\[\\p\{L\}\\p\{N\}\]\)\?/g, '')
            .replace(/\(\?\!\[\\p\{L\}\\p\{N\}\]\)\?/g, '')
            .replace(/\^/g, '')
            .replace(/\$/g, '')
            .replace(/\b/g, '')
            .toLowerCase();
        
        // Exact match
        if (tokensSet.has(cleanPattern)) return true;
        
        // Also check if cleanPattern contains any of the tokens (for composite tokens like "15-nov")
        for (const t of tokens) {
            if (cleanPattern.includes(t.toLowerCase())) return true;
        }
    }
    return false;
}

/**
 * Generate date patterns and integrate them into search conditions.
 * Caller MUST pass the already-computed recognizer result (from query-parser.js)
 * — this function does NOT call recognizeDate itself, to avoid double work on every keystroke.
 *
 * @param {Array} conditions - Existing search conditions from getMatchConditions
 * @param {string} queryOriginal - Original search query
 * @param {string|null} dateFromQuery - Parsed date from query (YYYY-MM-DD) or null
 * @param {string|null} dateToQuery - Parsed date to query (YYYY-MM-DD) or null
 * @param {Array|null} dateVariants - Array of ambiguous date interpretations or null
 * @param {Array|null} usedTokens - Array of tokens used for date recognition
 * @param {Object|null} [recognized] - Optional recognizer payload (preferred). When omitted, the
 *                                     function reconstructs a minimal payload from the explicit args.
 * @returns {Array} - Conditions with date patterns integrated
 */
export function integrateDatePatterns(conditions, queryOriginal, dateFromQuery, dateToQuery, dateVariants, usedTokens, recognized) {
    const datePatternsWithBoost = [];
    const pad2 = (n) => String(n).padStart(2, '0');

    // Build (or accept) recognizer payload without re-running recognizeDate.
    const hasDate = !!(dateFromQuery || dateToQuery || (dateVariants && dateVariants.length));
    const recognizedPayload = recognized || (hasDate ? {
        from: dateFromQuery || null,
        to: dateToQuery || null,
        variants: dateVariants || null,
        usedTokens: usedTokens || null,
        query: queryOriginal || ''
    } : null);

    if (recognizedPayload && typeof window.generateDatePatterns === 'function') {
        const smartPatterns = window.generateDatePatterns(recognizedPayload);
        if (smartPatterns && smartPatterns.length > 0) {
            datePatternsWithBoost.push(...smartPatterns);
            if (debugMode === '1') {
                console.log('🧠 [Smart-OR] Generated patterns from smart recognizer:', smartPatterns.length);
            }
        } else if (debugMode === '1') {
            console.log('🧠 [Smart-OR] generateDatePatterns returned empty array, recognized:', recognizedPayload);
        }
    }
    
    if (window.HyperLog && debugMode === '1' && (dateFromQuery || dateToQuery || (dateVariants && dateVariants.length > 0))) { 
        try { window.HyperLog.debug('[FilterManager] Dates found:', { dateFromQuery, dateToQuery, dateVariants: dateVariants ? dateVariants.length : 0 }); } catch(_){} 
    }
    
    // 1. Collect date patterns (legacy fallback if smart recognizer didn't generate patterns)
    if (datePatternsWithBoost.length === 0 && (dateFromQuery || dateToQuery)) {
        if (dateFromQuery && dateToQuery) {
            const fromParts = dateFromQuery.split('-');
            const toParts = dateToQuery.split('-');
            if (fromParts.length === 3 && toParts.length === 3) {
                const is4DigitQuery = queryOriginal && /^\d{4}$/.test(queryOriginal.trim());
                if (is4DigitQuery && fromParts[0] === toParts[0] && fromParts[1] === toParts[1]) {
                    const yearMonthPattern = `${fromParts[0]}-${fromParts[1]}-\\d{2}`;
                    datePatternsWithBoost.push({ pattern: yearMonthPattern, boost: BOOST_DATE, searchInDateField: true });
                    const day = parseInt(queryOriginal.slice(0, 2), 10);
                    const month = parseInt(queryOriginal.slice(2, 4), 10);
                    if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
                        const ddmmPattern = `\\d{4}-${pad2(month)}-${pad2(day)}`;
                        datePatternsWithBoost.push({ pattern: ddmmPattern, boost: BOOST_DATE, searchInDateField: true });
                    }
                } else if (fromParts[0] === toParts[0] && fromParts[1] === toParts[1] && fromParts[2] === toParts[2]) {
                    datePatternsWithBoost.push({ pattern: dateFromQuery, boost: BOOST_DATE, searchInDateField: true });
                } else if (fromParts[0] === toParts[0] && fromParts[1] === toParts[1]) {
                    const yearMonthPattern = `${fromParts[0]}-${fromParts[1]}-\\d{2}`;
                    datePatternsWithBoost.push({ pattern: yearMonthPattern, boost: BOOST_DATE, searchInDateField: true });
                } else if (fromParts[0] === toParts[0]) {
                    const yearPattern = `${fromParts[0]}-\\d{2}-\\d{2}`;
                    datePatternsWithBoost.push({ pattern: yearPattern, boost: BOOST_DATE, searchInDateField: true });
                } else if (fromParts[1] === toParts[1] && fromParts[2] === toParts[2]) {
                    const dayMonthPattern = `\\d{4}-${fromParts[1]}-${fromParts[2]}`;
                    datePatternsWithBoost.push({ pattern: dayMonthPattern, boost: BOOST_DATE, searchInDateField: true });
                } else {
                    datePatternsWithBoost.push({ pattern: `\\d{4}-\\d{2}-\\d{2}`, boost: BOOST_DATE, searchInDateField: true });
                }
            }
        } else if (dateFromQuery) {
            datePatternsWithBoost.push({ pattern: dateFromQuery, boost: BOOST_DATE, searchInDateField: true });
        } else if (dateToQuery) {
            datePatternsWithBoost.push({ pattern: dateToQuery, boost: BOOST_DATE, searchInDateField: true });
        }
    }
    
    if (dateVariants && Array.isArray(dateVariants)) {
        dateVariants.forEach(variant => {
            if (variant.pattern) {
                datePatternsWithBoost.push({ pattern: variant.pattern, boost: BOOST_DATE, searchInDateField: true });
            }
        });
    }

    if (datePatternsWithBoost.length === 0) return conditions;

    // 2. Integrate into conditions
    if (!Array.isArray(conditions) || conditions.length === 0) {
        return [{ condition: 'or', value: datePatternsWithBoost, modifier: 'i', isCategory: false }];
    }

    let addedToExisting = false;

    // Helper: replace conditions matching tokens with a combined (Date OR AND(Tokens)) condition
    const processAndCondition = (andCond) => {
        if (!andCond || !andCond.value || !Array.isArray(andCond.value)) return false;
        if (!usedTokens || usedTokens.length === 0) return false;
        
        const matchedIndices = [];
        for (let i = 0; i < andCond.value.length; i++) {
            if (matchesUsedTokens(andCond.value[i], usedTokens)) {
                matchedIndices.push(i);
            }
        }
        
        if (matchedIndices.length > 0) {
            const matchedConditions = matchedIndices.map(idx => andCond.value[idx]);
            const newOrCondition = {
                condition: 'or',
                value: [
                    ...datePatternsWithBoost,
                    {
                        condition: 'and',
                        value: matchedConditions,
                        isCategory: false
                    }
                ],
                modifier: 'i',
                isCategory: false
            };
            
            if (debugMode === '1') {
                console.log('🧠 [Smart-OR] Grouping tokens:', usedTokens);
                console.log('🧠 [Smart-OR] Created condition:', JSON.stringify(newOrCondition, null, 2));
            }

            matchedIndices.sort((a, b) => b - a);
            const firstIdx = matchedIndices[matchedIndices.length - 1];
            for (const idx of matchedIndices) { andCond.value.splice(idx, 1); }
            andCond.value.splice(firstIdx, 0, newOrCondition);
            return true;
        }
        return false;
    };

    // Try OR wrapper first (Pass 1 / Pass 2 structure)
    if (conditions[0] && conditions[0].condition === 'or' && conditions[0].value && Array.isArray(conditions[0].value)) {
        const isOrWrapper = conditions[0].value[0] && conditions[0].value[0].condition && 
            (conditions[0].value[0].condition === 'and' || conditions[0].value[0].condition === 'or');
        
        if (isOrWrapper) {
            // Apply to regular AND part (Pass 2)
            if (conditions[0].value.length >= 2 && conditions[0].value[1].condition === 'and') {
                if (processAndCondition(conditions[0].value[1])) {
                    addedToExisting = true;
                }
            }
        }
    }

    // Try direct AND condition
    if (!addedToExisting && conditions[0] && conditions[0].condition === 'and') {
        if (processAndCondition(conditions[0])) {
            addedToExisting = true;
        }
    }

    // Fallback: old behavior (add to numeric OR group)
    if (!addedToExisting && conditions[0] && conditions[0].condition === 'and' && conditions[0].value) {
        for (const subCond of conditions[0].value) {
            if (subCond.condition === 'or' && subCond.value && Array.isArray(subCond.value)) {
                if (!hasExactPhraseInOrCondition(subCond) && hasNumericPatterns(subCond)) {
                    for (const datePattern of datePatternsWithBoost) {
                        subCond.value.unshift(datePattern);
                    }
                    addedToExisting = true;
                    break;
                }
            }
        }
    }

    if (debugMode === '1') {
        console.log('📊 [FilterManager] Final conditions after date integration:', JSON.stringify(conditions, null, 2));
    }

    return conditions;
}
